import { useAutoTrim, useRevealChars } from "../../hooks/useRevealChars.ts";
import type { RevealMode } from "../../providers/themeProvider.tsx";

/**
 * The whitespace preference controls — "Trim spaces on save" and the two
 * "Reveal characters" rows — in one place so every surface
 * that offers them drives the SAME state rather than a copy. Rendered by the
 * user menu (Preferences…) and by the Modal Gallery toolbar, which exists purely
 * so all the modal primitives can be checked by flipping one control on one page.
 *
 * There is deliberately no local/preview mode: changing this anywhere changes it
 * everywhere, persists to localStorage, and survives navigation.
 *
 * See docs/reveal-input-characters.md.
 */

const MODE_TOOLTIP = [
  "Show whitespace and hidden characters in fields you type into.",
  "",
  "• Off    — normal.",
  "• Edges  — marks spaces at the start and end of a value, where they are",
  "           almost always an accident from copy-paste.",
  "• All    — additionally shows every space as a mid dot.",
  "",
  "Tab, non-breaking space, line breaks, control characters and zero-width",
  "characters are marked in both Edges and All.",
  "",
  "Nothing is changed, trimmed or blocked — this only affects how the value is",
  "drawn on screen. Fields render in a monospaced font while it is on.",
].join("\n");

const TEXTAREA_TOOLTIP = [
  "Extend the active mode to multi-line fields (notes, descriptions, raw JSON).",
  "",
  "Off by default: long free text legitimately contains a lot of whitespace, so",
  "the markers are noisiest there. Turn it on when chasing a bad paste.",
  "",
  "Only available while Reveal characters is on.",
].join("\n");

const TRIM_TOOLTIP = [
  "Remove leading and trailing spaces from a value when you save it.",
  "",
  "On by default: a space at the start or end is almost always a copy-paste",
  "accident. Turn it off to save a value exactly as typed — for the rare value",
  "where the space is real, or to reproduce a dirty value while testing.",
  "",
  "Applies to the kiosk settings grids.",
].join("\n");

const MODE_OPTIONS: { value: RevealMode; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "edges", label: "Edges (start/end)" },
  { value: "all", label: "All characters" },
];

const selectStyle: React.CSSProperties = {
  fontSize: "var(--text-xs)",
  padding: "var(--space-0-5) var(--space-1)",
  borderRadius: "var(--radius-1)",
  border: "1px solid var(--color-border-default)",
  background: "var(--color-bg)",
  color: "var(--color-text)",
};

export interface RevealCharsControlsProps {
  /**
   * Class applied to each row. The user menu passes its own `styles.menuItem` so
   * the rows sit flush with Density / Filter bar / Session state above them.
   */
  rowClassName?: string;
  /** "menu" = stacked label-left / control-right rows. "inline" = one toolbar row. */
  layout?: "menu" | "inline";
}

export function RevealCharsControls({ rowClassName, layout = "menu" }: RevealCharsControlsProps) {
  const { revealMode, setRevealMode, revealTextarea, setRevealTextarea } = useRevealChars();
  const { autoTrim, setAutoTrim } = useAutoTrim();
  const inline = layout === "inline";

  const trimCheckbox = (
    <input
      type="checkbox"
      checked={autoTrim}
      onChange={e => setAutoTrim(e.target.checked)}
      aria-label="Trim spaces on save"
    />
  );

  const modeSelect = (
    <select
      value={revealMode}
      onChange={e => setRevealMode(e.target.value as RevealMode)}
      style={selectStyle}
      aria-label="Reveal characters"
    >
      {MODE_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );

  const textareaCheckbox = (
    <input
      type="checkbox"
      checked={revealTextarea && revealMode !== "off"}
      disabled={revealMode === "off"}
      onChange={e => setRevealTextarea(e.target.checked)}
      aria-label="Include multi-line fields"
    />
  );

  if (inline) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-sm)" }} title={TRIM_TOOLTIP}>
          {trimCheckbox}
          <span>Trim spaces on save</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-sm)" }} title={MODE_TOOLTIP}>
          <span>Reveal characters</span>
          {modeSelect}
        </label>
        <label
          style={{
            display: "flex", alignItems: "center", gap: "var(--space-2)",
            fontSize: "var(--text-sm)", opacity: revealMode === "off" ? 0.5 : 1,
          }}
          title={TEXTAREA_TOOLTIP}
        >
          {textareaCheckbox}
          <span>Include multi-line fields</span>
        </label>
      </div>
    );
  }

  return (
    <>
      <div
        className={rowClassName}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
        title={TRIM_TOOLTIP}
      >
        <span>Trim spaces on save</span>
        {trimCheckbox}
      </div>
      <div
        className={rowClassName}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "default" }}
        title={MODE_TOOLTIP}
      >
        <span>Reveal characters</span>
        {modeSelect}
      </div>
      <div
        className={rowClassName}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "default", paddingLeft: "var(--space-4)",
          opacity: revealMode === "off" ? 0.5 : 1,
        }}
        title={TEXTAREA_TOOLTIP}
      >
        <span>Multi-line fields</span>
        {textareaCheckbox}
      </div>
    </>
  );
}
